import { Component, ElementRef, EventEmitter, HostBinding, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { DxPopoverComponent } from 'devextreme-angular';
import { ClickEvent } from 'devextreme/ui/button';
import { ShowingEvent } from 'devextreme/ui/popover';

const DEFAULT_COLOR = 'rgba(0, 0, 0, 0)';

const HEX_INPUT_MAX_LENGTH = 6;
const HEX_DIGITS_PATTERN = /[^0-9a-fA-F]/g;

@Component({
  selector: 'ag-shr-color-sketch-popover',
  templateUrl: './color-sketch-popover.component.html',
  styleUrl: './color-sketch-popover.component.scss',
})
export class ColorSketchPopoverComponent implements OnDestroy {
  @HostBinding('class') className = 'color-sketch-popover';

  @ViewChild('hexGuardRoot', { read: ElementRef }) private hexGuardRoot?: ElementRef<HTMLElement>;

  @Input() target: Element;
  @Input() container: Element;
  @Input() set value(data: string | undefined) {
    this.selectedColor = data ?? DEFAULT_COLOR;
  }
  @Output() valueChange = new EventEmitter<string>();
  @Input() presetColors = [{ value: 'transparent', description: 'Transparent', hint: '' }];
  @Input() handleOutsidePopoverClick: (event: any) => boolean;
  @Output() onApplyColor = new EventEmitter();
  @Output() onCancelColorChanges = new EventEmitter();
  @Output() onShowing = new EventEmitter<ShowingEvent>();

  protected toggleButtonOptions = {
    icon: 'chevrondown',
    onClick: () => {
      this.togglePicker();
    },
    hint: 'Show color picker',
    stylingMode: 'text',
  };
  protected selectedColor = DEFAULT_COLOR;
  // color-sketch internal inputs can keep stale state after cancel, so need to re-render
  protected colorSketchRenderTick = 0;
  private openedColor = DEFAULT_COLOR;
  protected showHexValidationMessage = false;
  protected hexInputTarget: HTMLElement | null = null;
  private isHexInputIncomplete = false;
  private rawHexInputValue = '';
  private _hexRestoreTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private _hexBlurTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private hexGuardsHost: HTMLElement | null = null;

  constructor() {}

  ngOnDestroy() {
    this.clearTimeouts();
    this.detachHexFieldGuards();
  }

  protected togglePicker = () => {};

  protected applyColorChanges = (e: ClickEvent, component: DxPopoverComponent) => {
    e.event.stopPropagation();

    if (this.isHexInputIncomplete) {
      this.showHexValidationMessage = true;

      this.clearTimeouts();

      // Keep the exact user-entered HEX value (e.g. "abc") instead of auto-normalized "aabbcc".
      const hexInput = this.hexInputTarget as HTMLInputElement | null;
      if (hexInput && this.rawHexInputValue) {
        this._hexRestoreTimeoutId = setTimeout(() => {
          hexInput.value = this.rawHexInputValue;
        });
      }
      return;
    }

    this.showHexValidationMessage = false;
    this.valueChange.emit(this.selectedColor);

    component.instance.hide();
  };

  protected cancelColorChanges = (e: ClickEvent, component: DxPopoverComponent) => {
    e.event.stopPropagation();
    this.selectedColor = this.openedColor;
    this.isHexInputIncomplete = false;
    this.rawHexInputValue = '';
    this.showHexValidationMessage = false;
    this.clearTimeouts();
    this.refreshColorSketch();
    this.onCancelColorChanges.emit();

    component.instance.hide();
  };

  protected handleShowing = (e: ShowingEvent) => {
    this.openedColor = this.selectedColor ?? DEFAULT_COLOR;
    this.showHexValidationMessage = false;
    this.refreshColorSketch();
    // guards attached after open (queueMicrotask + setTimeout(0) fallback if ViewChild isn’t ready yet)
    queueMicrotask(() => {
      this.attachHexFieldGuards();
      if (!this.hexGuardsHost) {
        setTimeout(() => this.attachHexFieldGuards(), 0);
      }
    });
    this.onShowing.emit(e);
  };

  /**
   * ngx-color sketch fields only emit (onChange) when isValidHex() passes, so invalid text
   * (e.g. "ggg") never reaches handleChange. Enforce hex at the native input via capture listeners.
   */
  private readonly onHexFieldKeydown = (e: KeyboardEvent) => {
    if (!this.isSketchHexInput(e.target) || e.isComposing) {
      return;
    }
    if (this.isHexFieldNavigationOrShortcutKey(e)) {
      return;
    }
    const input = e.target as HTMLInputElement;
    if (e.key.length === 1 && /[0-9a-fA-F]/.test(e.key)) {
      const start = input.selectionStart ?? 0;
      const end = input.selectionEnd ?? 0;
      const projectedLen = input.value.length - (end - start) + 1;
      if (projectedLen > HEX_INPUT_MAX_LENGTH) {
        e.preventDefault();
      }
      return;
    }
    if (e.key.length === 1) {
      e.preventDefault();
    }
  };

  private readonly onHexFieldPaste = (e: ClipboardEvent) => {
    if (!this.isSketchHexInput(e.target)) {
      return;
    }
    e.preventDefault();
    const input = e.target as HTMLInputElement;
    const paste = e.clipboardData?.getData('text') ?? '';
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    const merged = this.sanitizeHexInputDigits(input.value.slice(0, start) + paste + input.value.slice(end));
    if (merged !== input.value) {
      input.value = merged;
    }
    const insertLen = this.sanitizeHexInputDigits(paste).length;
    const newCursor = Math.min(start + insertLen, merged.length);
    queueMicrotask(() => {
      input.setSelectionRange(newCursor, newCursor);
      this.commitSketchHexToNgxColor(input);
    });
  };

  private readonly onHexFieldInput = (e: Event) => {
    if (!(e instanceof InputEvent) || e.isComposing || !this.isSketchHexInput(e.target)) {
      return;
    }
    const input = e.target as HTMLInputElement;
    const sanitized = this.sanitizeHexInputDigits(input.value);
    if (sanitized === input.value) {
      return;
    }
    const start = input.selectionStart ?? sanitized.length;
    const newCursor = Math.min(this.sanitizeHexInputDigits(input.value.slice(0, start)).length, sanitized.length);
    input.value = sanitized;
    queueMicrotask(() => {
      input.setSelectionRange(newCursor, newCursor);
      this.commitSketchHexToNgxColor(input);
    });
  };

  protected handleChange = e => {
    const inputValue = e?.$event?.target?.value;
    const inputTarget = e?.$event?.target as HTMLElement;
    const color = e?.color;
    const source = color?.source;
    const opacity = color?.rgb?.a ?? 1;
    const hexInput = source === 'hex' && inputTarget ? (inputTarget as HTMLInputElement) : null;
    let hex = color?.hex;

    if (hexInput) {
      this.hexInputTarget = inputTarget;
      this.rawHexInputValue = hexInput.value;

      hexInput.removeEventListener('blur', this.handleHexBlur as EventListener);

      this._hexBlurTimeoutId = setTimeout(() => {
        hexInput.addEventListener('blur', this.handleHexBlur as EventListener);
      });

      const baseDigits = hexInput.value.toLowerCase().slice(0, HEX_INPUT_MAX_LENGTH);
      if (baseDigits.length === HEX_INPUT_MAX_LENGTH) {
        const prefix = color?.hex != null && !String(color.hex).startsWith('#') ? '' : '#';
        hex = `${prefix}${baseDigits}`;
      }
    }

    const hexFieldLength = hexInput ? hexInput.value.length : String(inputValue ?? '').length;
    this.isHexInputIncomplete = source === 'hex' && hexFieldLength < HEX_INPUT_MAX_LENGTH;

    if (this.isHexInputIncomplete) {
      return;
    }

    this.showHexValidationMessage = false;

    if (color?.source != 'hsva' && opacity !== 1) {
      const alphaValue = Math.round(opacity * 255);
      const alphaHex = alphaValue.toString(16).padStart(2, '0');

      hex += alphaHex;
    }

    this.applyColor(hex);
  };

  protected applyColor = (hex: string) => {
    if (hex === this.selectedColor) {
      return;
    }

    this.isHexInputIncomplete = false;
    this.showHexValidationMessage = false;
    this.clearTimeouts();
    this.onApplyColor.emit(hex);

    this.selectedColor = hex;
  };

  // fix ngx-color problem with chaange color on blur (for example from aaa to AAAAAA etc.)
  protected handleHexBlur = () => {
    if (this.isHexInputIncomplete && this.hexInputTarget && this.rawHexInputValue) {
      this.clearTimeouts();
      this._hexBlurTimeoutId = setTimeout(() => {
        (this.hexInputTarget as HTMLInputElement).value = this.rawHexInputValue;
      }, 0);
    }
  };

  private clearTimeouts() {
    if (this._hexRestoreTimeoutId) {
      clearTimeout(this._hexRestoreTimeoutId);
      this._hexRestoreTimeoutId = null;
    }

    if (this._hexBlurTimeoutId) {
      clearTimeout(this._hexBlurTimeoutId);
      this._hexBlurTimeoutId = null;
    }
  }

  private refreshColorSketch = () => {
    this.colorSketchRenderTick += 1;
  };

  private sanitizeHexInputDigits(raw: string): string {
    return raw.replace(HEX_DIGITS_PATTERN, '').slice(0, HEX_INPUT_MAX_LENGTH);
  }

  private attachHexFieldGuards(): void {
    const el = this.hexGuardRoot?.nativeElement;
    if (!el) {
      return;
    }
    if (this.hexGuardsHost === el) {
      return;
    }
    this.detachHexFieldGuards();
    el.addEventListener('keydown', this.onHexFieldKeydown, true);
    el.addEventListener('paste', this.onHexFieldPaste, true);
    el.addEventListener('input', this.onHexFieldInput, true);
    this.hexGuardsHost = el;
  }

  private detachHexFieldGuards(): void {
    const el = this.hexGuardsHost;
    if (!el) {
      return;
    }
    el.removeEventListener('keydown', this.onHexFieldKeydown, true);
    el.removeEventListener('paste', this.onHexFieldPaste, true);
    el.removeEventListener('input', this.onHexFieldInput, true);
    this.hexGuardsHost = null;
  }

  private isSketchHexInput(target: EventTarget | null): target is HTMLInputElement {
    if (!target || (target as HTMLElement).tagName !== 'INPUT') {
      return false;
    }
    return !!(target as HTMLElement).closest?.('.sketch-double');
  }

  private isHexFieldNavigationOrShortcutKey(e: KeyboardEvent): boolean {
    if (e.ctrlKey || e.metaKey || e.altKey) {
      return true;
    }
    return (
      e.key === 'Backspace' ||
      e.key === 'Delete' ||
      e.key === 'Tab' ||
      e.key === 'Escape' ||
      e.key === 'Enter' ||
      e.key === 'ArrowLeft' ||
      e.key === 'ArrowRight' ||
      e.key === 'ArrowUp' ||
      e.key === 'ArrowDown' ||
      e.key === 'Home' ||
      e.key === 'End'
    );
  }

  /** ngx-color editable-input only reacts on keyup, not input. */
  private commitSketchHexToNgxColor(input: HTMLInputElement): void {
    input.dispatchEvent(new KeyboardEvent('keyup', { bubbles: true, key: 'Unidentified' }));
  }
}
